/**
 * UpdatesPage
 * Gestión de actualizaciones remotas de agentes con progreso en tiempo real.
 * Lista de agentes en formato tabla (mismo formato que la versión original),
 * con búsqueda, filtro por estado de versión y orden por columna. Las dos
 * secciones principales (versión disponible y estado por agente) viven
 * dentro de Cards para respetar el lenguaje visual del resto de la app.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Stack,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  IconButton,
  Popover,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  SystemUpdate as UpdateIcon,
  CheckCircle as UpToDateIcon,
  Warning as OutdatedIcon,
  CloudOff as OfflineIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Search as SearchIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { useAgents } from '@/hooks/useAgents';
import { useAgentsStore } from '@/store/agentsStore';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import { UpdateProgressCell } from '@/components/agents/UpdateProgressCell';
import {
  agentVersionStatus,
  filterAndSortAgents,
  type AgentVersionStatus,
  type StatusFilter,
  type SortKey,
  type SortOrder,
} from '@/lib/updates/filterAndSortAgents';
import {
  effectiveConnection,
  isActiveUpdateStatus,
  labelForStatus,
} from '@/lib/updates/updateDisplay';

interface UpdateManifest {
  version: string;
  notes?: string;
  channel?: string;
  assets: Array<{
    os: string;
    arch: string;
    url: string;
    sha256?: string;
    kind?: string;
  }>;
}

const versionStatusConfig: Record<
  AgentVersionStatus,
  { label: string; color: 'success' | 'warning' | 'default' | 'error' }
> = {
  'up-to-date': { label: 'Al día', color: 'success' },
  outdated: { label: 'Desactualizado', color: 'warning' },
  unknown: { label: 'Desconocido', color: 'default' },
  offline: { label: 'Offline', color: 'default' },
};

// Detalle del flujo de actualización. Mostrado bajo demanda en un Popover (no inline)
// para minimizar el espacio ocupado en la página.
const HOW_IT_WORKS_DETAIL = (
  <>
    Al presionar <strong>Actualizar</strong>, el agente verifica el manifiesto, descarga el binario,
    verifica SHA256 y lo reemplaza con rollback automático ante fallo. El progreso se muestra en
    tiempo real vía WebSocket. Configure <code>AGENT_UPDATE_VERSION</code> y{' '}
    <code>AGENT_UPDATE_*_URL</code> en el backend para el canal stable; use el prefijo{' '}
    <code>AGENT_UPDATE_BETA_</code> para el canal beta.
  </>
);

export const UpdatesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState<'stable' | 'beta'>('stable');
  const [triggerResults, setTriggerResults] = useState<Record<string, 'sent' | 'error'>>({});
  const [howItWorksAnchor, setHowItWorksAnchor] = useState<HTMLElement | null>(null);

  // Estado local de filtros / búsqueda / orden. No se persiste a la store
  // global de agentes porque la semántica es distinta (filtro por estado de
  // versión, no por conexión; sort por versión, no por hostname).
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortKey>('hostname');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const { data: agents = [], isLoading: agentsLoading } = useAgents();
  const updateStates = useAgentsStore((s) => s.updateStates);

  const {
    data: manifest,
    isLoading: manifestLoading,
    error: manifestError,
    refetch: refetchManifest,
  } = useQuery<UpdateManifest>({
    queryKey: ['agent-update-manifest', channel],
    queryFn: async () => {
      // portable=true: versión CalVer del .exe portable (la flota corre el portable
      // como servicio). El backend ignora el canal para el manifiesto portable.
      const res = await apiClient.get<UpdateManifest>(
        `/agent-update?channel=${channel}&portable=true`
      );
      return res.data;
    },
    // El manifiesto se arma en vivo desde GitHub: una falla puntual no debe dejar
    // la página en "Desconocido". Reintentar y refrescar periódicamente.
    retry: 2,
    retryDelay: 1500,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const sendUpdate = useMutation({
    mutationFn: async (agentId: string) => {
      await apiClient.post(`/agents/${agentId}/commands`, {
        command: 'check_update',
      });
    },
    onSuccess: (_, agentId) => {
      setTriggerResults((prev) => ({ ...prev, [agentId]: 'sent' }));
      setTimeout(() => {
        setTriggerResults((prev) => {
          const next = { ...prev };
          delete next[agentId];
          return next;
        });
      }, 8000);
    },
    onError: (_, agentId) => {
      setTriggerResults((prev) => ({ ...prev, [agentId]: 'error' }));
    },
  });

  const triggerAllOutdated = useCallback(() => {
    const outdated = agents.filter(
      (a) => a.connected && agentVersionStatus(a, manifest?.version) === 'outdated'
    );
    outdated.forEach((a) => sendUpdate.mutate(a.agentId));
  }, [agents, manifest, sendUpdate]);

  // Lista derivada: aplica filtro por estado + búsqueda + orden. useMemo para
  // evitar recalcular en cada render cuando ninguno de los inputs cambió.
  // La lógica vive en lib/updates/filterAndSortAgents.ts para poder testearla
  // sin DOM (pura, sin React Query ni hooks).
  const filteredAgents = useMemo(
    () =>
      filterAndSortAgents(agents, {
        search,
        statusFilter,
        sortBy,
        sortOrder,
        latestVersion: manifest?.version,
      }),
    [agents, manifest, search, statusFilter, sortBy, sortOrder]
  );

  // Métricas del summary siempre reflejan la flota completa (no la vista filtrada),
  // así no se confunde el operador con conteos distintos entre el header y la tabla.
  const connectedAgents = agents.filter((a) => a.connected);
  const outdatedCount = connectedAgents.filter(
    (a) => agentVersionStatus(a, manifest?.version) === 'outdated'
  ).length;
  const activeUpdates = Object.values(updateStates).filter((s) =>
    isActiveUpdateStatus(s.status)
  ).length;
  const completedUpdates = Object.values(updateStates).filter(
    (s) => s.status === 'complete'
  ).length;
  const failedUpdates = Object.values(updateStates).filter((s) => s.status === 'error').length;

  // Icono "Cómo funciona" inline al lado del título. Hover → Tooltip corto;
  // click → Popover anclado con el detalle. Ocupa ~32px y no agrega Card nueva.
  const howItWorksTitleTrailing = (
    <>
      <Tooltip title="Cómo funciona" arrow>
        <IconButton
          size="small"
          aria-label="Cómo funciona"
          onClick={(e) => setHowItWorksAnchor(e.currentTarget)}
          sx={{ color: 'text.secondary' }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(howItWorksAnchor)}
        anchorEl={howItWorksAnchor}
        onClose={() => setHowItWorksAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              maxWidth: 420,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
            },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Cómo funciona
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {HOW_IT_WORKS_DETAIL}
        </Typography>
      </Popover>
    </>
  );

  return (
    // Fill remaining viewport height (set up by MainLayout's flex column <main>)
    // so the agent table scrolls internally and the body never does. `flex: 1`
    // + `minHeight: 0` lets this Box shrink below its content size — without
    // `minHeight: 0` flex items default to `min-height: auto` and ignore
    // `overflow: auto` on their children.
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
      }}
    >
      {/* Pinned header — title, manifest, summary. Never scrolls. */}
      <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PageHeader
          icon={<UpdateIcon />}
          title="Actualizaciones"
          subtitle="Gestión de versiones y actualizaciones remotas de agentes."
          titleTrailing={howItWorksTitleTrailing}
          actions={
            <>
              <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
                <InputLabel>Canal</InputLabel>
                <Select
                  value={channel}
                  label="Canal"
                  onChange={(e) => setChannel(e.target.value as 'stable' | 'beta')}
                >
                  <MenuItem value="stable">Stable</MenuItem>
                  <MenuItem value="beta">Beta</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  refetchManifest();
                  queryClient.invalidateQueries({ queryKey: ['agents'] });
                }}
              >
                Actualizar
              </Button>
            </>
          }
        />

        {/* Versión disponible — Card como wrapper */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Versión disponible
              {channel === 'beta' && (
                <Chip label="BETA" color="warning" size="small" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {manifestLoading ? (
              <CircularProgress size={24} />
            ) : manifestError ? (
              <Alert severity="info">
                No hay manifiesto configurado para el canal <strong>{channel}</strong>. Configure{' '}
                <code>AGENT_UPDATE_VERSION</code> y <code>AGENT_UPDATE_*_URL</code> en el backend.
              </Alert>
            ) : manifest ? (
              <Stack spacing={1}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body1">
                    <strong>Versión disponible:</strong>
                  </Typography>
                  <Chip label={`v${manifest.version}`} color="primary" />
                </Stack>
                {manifest.notes && (
                  <Typography variant="body2" color="text.secondary">
                    {manifest.notes}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Plataformas: {manifest.assets.map((a) => `${a.os}/${a.arch}`).join(', ')}
                </Typography>
              </Stack>
            ) : null}
          </CardContent>
        </Card>

        {/* Rollout summary */}
        {manifest && (
          <Card>
            <CardContent>
              <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                <Box>
                  <Typography variant="h5">{agents.length}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h5" color="success.main">
                    {connectedAgents.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Online
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    color={outdatedCount > 0 ? 'warning.main' : 'success.main'}
                  >
                    {outdatedCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Desactualizados
                  </Typography>
                </Box>
                {activeUpdates > 0 && (
                  <Box>
                    <Typography variant="h5" color="info.main">
                      {activeUpdates}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      En progreso
                    </Typography>
                  </Box>
                )}
                {completedUpdates > 0 && (
                  <Box>
                    <Typography variant="h5" color="success.main">
                      {completedUpdates}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Completados
                    </Typography>
                  </Box>
                )}
                {failedUpdates > 0 && (
                  <Box>
                    <Typography variant="h5" color="error.main">
                      {failedUpdates}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fallidos
                    </Typography>
                  </Box>
                )}
                {outdatedCount > 0 && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<UpdateIcon />}
                    onClick={triggerAllOutdated}
                    disabled={sendUpdate.isPending}
                  >
                    Actualizar todos ({outdatedCount})
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Scrollable region — only the agent table card scrolls. `pr: 1` keeps
          the scrollbar from sitting on top of the table content. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
        <Card sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Header con título + toolbar de búsqueda/filtro/orden */}
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Typography variant="h6">
                Estado por agente
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({filteredAgents.length}
                  {filteredAgents.length !== agents.length && ` de ${agents.length}`})
                </Typography>
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
              >
                <TextField
                  size="small"
                  placeholder="Buscar hostname, ID, OS o versión"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ minWidth: { xs: '100%', sm: 220 } }}
                />
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Estado"
                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="needs-update">Necesitan actualización</MenuItem>
                    <MenuItem value="up-to-date">Al día</MenuItem>
                    <MenuItem value="offline">Offline</MenuItem>
                    <MenuItem value="unknown">Desconocido</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                  <InputLabel>Ordenar por</InputLabel>
                  <Select
                    value={sortBy}
                    label="Ordenar por"
                    onChange={(e) => setSortBy(e.target.value as SortKey)}
                  >
                    <MenuItem value="hostname">Hostname</MenuItem>
                    <MenuItem value="versionStatus">Estado versión</MenuItem>
                    <MenuItem value="os">Sistema</MenuItem>
                    <MenuItem value="version">Versión</MenuItem>
                    <MenuItem value="lastSeen">Última conexión</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title={sortOrder === 'asc' ? 'Ascendente' : 'Descendente'}>
                  <IconButton
                    size="small"
                    aria-label="Cambiar orden ascendente/descendente"
                    onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                  >
                    {sortOrder === 'asc' ? <ArrowUpIcon /> : <ArrowDownIcon />}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Box>
          <Divider />

          {agentsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : agents.length === 0 ? (
            <EmptyState
              compact
              icon={<OfflineIcon />}
              title="No hay agentes registrados"
              description="Cuando un agente se conecte por primera vez aparecerá acá para que puedas actualizarlo."
            />
          ) : filteredAgents.length === 0 ? (
            <EmptyState
              compact
              icon={<SearchIcon />}
              title="Sin coincidencias"
              description="Probá con otro término de búsqueda o cambiá el filtro de estado."
            />
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Agente</TableCell>
                    <TableCell>Sistema</TableCell>
                    <TableCell>Versión actual</TableCell>
                    <TableCell>Estado versión</TableCell>
                    <TableCell>Progreso update</TableCell>
                    <TableCell align="right">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAgents.map((agent) => {
                    const versionSt = agentVersionStatus(agent, manifest?.version);
                    const versionCfg = versionStatusConfig[versionSt];
                    const updateState = updateStates[agent.agentId] ?? null;
                    const triggerResult = triggerResults[agent.agentId];
                    const isUpdating = isActiveUpdateStatus(updateState?.status);
                    // Si hay un update en curso, el agente puede estar offline
                    // momentáneamente por el reinicio. Mostramos la fase del
                    // update en el chip en vez de "Offline" para no confundir
                    // al operador con una desconexión real.
                    const effective = effectiveConnection(agent, updateState);
                    const showUpdateChip = effective === 'updating';
                    const versionLabel = showUpdateChip
                      ? labelForStatus(updateState!.status)
                      : versionCfg.label;
                    const versionColor = showUpdateChip ? 'info' : versionCfg.color;
                    const versionIcon = showUpdateChip ? (
                      <UpdateIcon fontSize="small" />
                    ) : versionSt === 'up-to-date' ? (
                      <UpToDateIcon fontSize="small" />
                    ) : versionSt === 'outdated' ? (
                      <OutdatedIcon fontSize="small" />
                    ) : (
                      <OfflineIcon fontSize="small" />
                    );

                    return (
                      <TableRow key={agent.agentId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {agent.hostname || agent.agentId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {agent.agentId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {agent.os || '—'} {agent.arch || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {agent.version ? `v${agent.version}` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={versionIcon}
                            label={versionLabel}
                            color={versionColor}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <UpdateProgressCell
                            state={updateState}
                            agent={agent}
                            latestVersion={manifest?.version}
                            onRetry={() => sendUpdate.mutate(agent.agentId)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {isUpdating ? (
                            <CircularProgress size={20} />
                          ) : triggerResult === 'sent' ? (
                            <Chip label="Enviado" color="success" size="small" />
                          ) : triggerResult === 'error' ? (
                            <Chip label="Error al enviar" color="error" size="small" />
                          ) : (
                            <Tooltip
                              title={
                                !agent.connected
                                  ? 'Agente offline'
                                  : versionSt === 'up-to-date'
                                    ? 'El agente ya está actualizado'
                                    : 'Enviar señal de actualización al agente (el agente verifica la versión)'
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<UpdateIcon />}
                                  // El agente hace su propio chequeo de versión server-side:
                                  // no exigimos el manifiesto del front para permitir disparar.
                                  disabled={!agent.connected || versionSt === 'up-to-date'}
                                  onClick={() => sendUpdate.mutate(agent.agentId)}
                                >
                                  Actualizar
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Box>
    </Box>
  );
};
